import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { ExternalLink } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { IridescentAvatar } from '@/components/IridescentAvatar';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { type DittoTheme, parseDittoTheme } from '@/lib/dittoTheme';
import { genUserName } from '@/lib/genUserName';
import { getPastelStripColor } from '@/lib/polaroid-utils';
import { useNostrPublish } from '@/hooks/useNostrPublish';

/** Kind numbers matching the ditto-5 theme system. */
const THEME_DEFINITION_KIND = 36767;
const ACTIVE_THEME_KIND = 16767;

const PAGE_SIZE = 20;

/**
 * Parse a theme event (kind 36767 or 16767) into display data.
 */
function parseThemeEvent(event: NostrEvent): {
  theme: DittoTheme;
  title: string;
  description?: string;
} | null {
  const theme = parseDittoTheme(event);
  if (!theme) return null;

  const title =
    event.tags.find(([n]) => n === 'title')?.[1] ??
    (event.kind === ACTIVE_THEME_KIND ? 'Profile Theme' : 'Untitled Theme');
  const description = event.tags.find(([n]) => n === 'description')?.[1];

  return { theme, title, description };
}

/**
 * Hook to fetch a global feed of theme definition events (kind 36767).
 */
function useGlobalThemeFeed() {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['global-theme-feed'],
    queryFn: async ({ pageParam }) => {
      const signal = AbortSignal.timeout(5000);
      const until = pageParam as number | undefined;

      const events = await nostr.query(
        [{
          kinds: [THEME_DEFINITION_KIND],
          limit: PAGE_SIZE,
          ...(until ? { until } : {}),
        }],
        { signal },
      );

      const seen = new Set<string>();
      return events
        .filter((event) => {
          if (seen.has(event.id)) return false;
          seen.add(event.id);
          return parseDittoTheme(event) !== null;
        })
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, PAGE_SIZE);
    },
    getNextPageParam: (lastPage: NostrEvent[]) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 2 * 60 * 1000,
  });
}

export default function ThemesPage() {
  const { user } = useCurrentUser();
  const { themesEnabled, setThemesEnabled, activeTheme, hasBgImage, setLocalTheme } = useDittoThemeContext();
  const { toast } = useToast();
  const { mutateAsync: publishEvent } = useNostrPublish();

  const {
    data: feedData,
    isLoading: feedLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGlobalThemeFeed();

  // Flatten pages
  const themeEvents = useMemo(() => {
    const seen = new Set<string>();
    return (feedData?.pages.flat() ?? []).filter((event) => {
      if (seen.has(event.id)) return false;
      seen.add(event.id);
      return true;
    });
  }, [feedData?.pages]);

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /** Apply a theme — saves locally for instant preview, and also publishes
   *  to Nostr (kind 16767) when logged in so it persists across devices. */
  const handleApplyTheme = useCallback(
    async (event: NostrEvent) => {
      const parsed = parseThemeEvent(event);
      if (!parsed) return;

      if (!themesEnabled) {
        setThemesEnabled(true);
      }

      // Always save to localStorage — works for everyone, instant effect
      setLocalTheme(parsed.theme);

      // If logged in, also publish to Nostr so the theme persists across devices
      if (user) {
        try {
          const tags: string[][] = [];

          for (const tag of event.tags) {
            if (tag[0] === 'c' || tag[0] === 'f' || tag[0] === 'bg') {
              tags.push([...tag]);
            }
          }

          const title = event.tags.find(([n]) => n === 'title')?.[1];
          if (title) tags.push(['title', title]);

          if (event.kind === THEME_DEFINITION_KIND) {
            const dTag = event.tags.find(([n]) => n === 'd')?.[1];
            if (dTag) {
              tags.push(['a', `${THEME_DEFINITION_KIND}:${event.pubkey}:${dTag}`]);
              tags.push(['k', String(THEME_DEFINITION_KIND)]);
            }
          }

          tags.push(['alt', 'Active profile theme']);

          await publishEvent({
            kind: ACTIVE_THEME_KIND,
            content: '',
            tags,
            created_at: Math.floor(Date.now() / 1000),
          });
        } catch (error) {
          console.error('Failed to publish theme to Nostr:', error);
          // Theme still works locally — just won't sync across devices
        }
      }

      toast({
        title: 'Theme applied!',
        description: `"${parsed.title}" is now your active theme.`,
      });
    },
    [user, themesEnabled, setThemesEnabled, setLocalTheme, publishEvent, toast],
  );

  useSeoMeta({
    title: 'Theme — Polaroids',
    description: 'Customize your Polaroids experience with community themes.',
  });

  // Determine if text needs paper-strip treatment for legibility
  const needsStrip = hasBgImage || (themesEnabled && !!activeTheme);

  return (
    <AppShell>
      <div className="py-6 space-y-6">
        {/* Page title — handwritten style */}
        <div className="text-center">
          <h1
            className="font-hand text-4xl font-bold text-pol-text inline-block"
            style={needsStrip ? { backgroundColor: 'rgb(var(--pol-bg))', padding: '2px 12px' } : { backgroundColor: getPastelStripColor('themes-title'), padding: '2px 12px' }}
          >
            Theme
          </h1>
          <p
            className="text-sm text-pol-text-secondary mt-2 inline-block"
            style={needsStrip ? { backgroundColor: 'rgb(var(--pol-bg))', padding: '1px 8px' } : {}}
          >
            Personalize your Polaroids look
          </p>
        </div>

        {/* Enable/Disable toggle — polaroid card style, no rounded corners */}
        <div
          className="relative bg-polaroid-white p-4 shadow-[0_2px_8px_rgba(45,27,78,0.08)]"
          style={{ transform: 'rotate(-0.5deg)' }}
        >
          {/* Mini tape strip at top */}
          <div
            className="absolute -top-[10px] left-8 w-[80px] h-[18px] tape-stripes-mint z-10"
            style={{ transform: 'rotate(1deg)' }}
          />

          <div className="flex items-center justify-between gap-4 relative">
            <div className="min-w-0">
              <p className="text-sm font-hand text-lg text-pol-text">Use themes</p>
              <p className="text-xs text-pol-text-secondary mt-0.5 font-sans">
                {themesEnabled
                  ? 'Themes are active. Toggle off for the default look.'
                  : 'Themes are off. Toggle on to show your theme.'}
              </p>
            </div>
            <Switch
              checked={themesEnabled}
              onCheckedChange={setThemesEnabled}
            />
          </div>
        </div>

        {/* Current theme preview (when enabled) — full mockup with bg image */}
        {themesEnabled && activeTheme && (
          <div className="relative" style={{ transform: 'rotate(0.3deg)' }}>
            <p
              className="font-hand text-sm font-bold text-pol-text-secondary uppercase tracking-wider mb-2 inline-block"
              style={needsStrip ? { backgroundColor: 'rgb(var(--pol-bg))', padding: '1px 8px' } : { backgroundColor: getPastelStripColor('current-label'), padding: '1px 8px' }}
            >
              Current Theme
            </p>
            <div className="bg-polaroid-white p-2 pb-3 shadow-[0_2px_8px_rgba(45,27,78,0.08)]">
              {/* Full mockup — same as feed theme cards */}
              <ThemeMockup theme={activeTheme} />
              <div className="px-1 pt-2">
                <p className="font-hand text-pol-text text-lg">{activeTheme.title ?? 'Your Theme'}</p>
                {activeTheme.fonts?.body && (
                  <p className="text-[11px] text-pol-text-secondary font-sans">{activeTheme.fonts.body.family}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Choose Theme feed */}
        <div className="space-y-3">
          <h2
            className="font-hand text-2xl font-bold text-pol-text inline-block"
            style={needsStrip ? { backgroundColor: 'rgb(var(--pol-bg))', padding: '2px 10px' } : { backgroundColor: getPastelStripColor('choose-heading'), padding: '2px 10px' }}
          >
            Choose a theme
          </h2>
          <p
            className="text-xs text-pol-text-secondary inline-block"
            style={needsStrip ? { backgroundColor: 'rgb(var(--pol-bg))', padding: '1px 8px' } : {}}
          >
            Browse themes from the community. Tap to apply.
          </p>
          <br />
          <a
            href="https://ditto.pub/themes"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-pol-text-secondary hover:text-pol-text transition-colors inline-flex items-center gap-1"
            style={needsStrip ? { backgroundColor: 'rgb(var(--pol-bg))', padding: '1px 8px' } : {}}
          >
            Make and share themes on Ditto! <ExternalLink size={12} />
          </a>

          {feedLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <ThemeCardSkeleton key={i} />
              ))}
            </div>
          ) : themeEvents.length === 0 ? (
            <div className="bg-polaroid-white p-6 text-center border-2 border-dashed border-cream-dark/50">
              <p className="text-pol-text-secondary text-sm font-hand text-lg">
                No themes found yet. Check back later!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {themeEvents.map((event, i) => (
                <ThemeCard
                  key={event.id}
                  event={event}
                  index={i}
                  onApply={handleApplyTheme}
                />
              ))}

              {/* Sentinel — always rendered so the ref is stable; hidden when no more pages */}
              <div ref={sentinelRef} className="h-px" />

              {isFetchingNextPage && (
                <div className="space-y-4">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <ThemeCardSkeleton key={`loading-${i}`} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

/* ── ThemeCard — polaroid-style, no rounded corners ── */

function ThemeCard({ event, index, onApply }: { event: NostrEvent; index: number; onApply: (event: NostrEvent) => void }) {
  const parsed = parseThemeEvent(event);
  if (!parsed) return null;

  const { theme, title, description } = parsed;
  const rotation = ((index % 5) - 2) * 0.6;

  return (
    <button
      onClick={() => onApply(event)}
      className="w-full text-left bg-polaroid-white p-2 pb-3 shadow-[0_2px_8px_rgba(45,27,78,0.08)] transition-all hover:shadow-[0_4px_16px_rgba(45,27,78,0.14)] hover:-translate-y-0.5 active:scale-[0.98] group"
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      {/* Color mockup preview */}
      <ThemeMockup theme={theme} />

      {/* Info row */}
      <div className="px-1 pt-2">
        <div className="flex items-center gap-2.5">
          <AuthorAvatar pubkey={event.pubkey} />
          <div className="min-w-0 flex-1">
            <p className="font-hand text-lg text-pol-text truncate group-hover:text-pol-pink transition-colors leading-tight">
              {title}
            </p>
            <AuthorName pubkey={event.pubkey} />
          </div>
          {/* Apply hint */}
          <span className="shrink-0 font-hand text-sm text-pol-pink opacity-0 group-hover:opacity-100 transition-opacity">
            apply →
          </span>
        </div>
        {description && (
          <p className="text-xs text-pol-text-secondary mt-1 line-clamp-2 font-sans">{description}</p>
        )}
      </div>
    </button>
  );
}

/* ── Theme mockup — sharp corners, photo-like ── */

function ThemeMockup({ theme }: { theme: DittoTheme }) {
  const { colors, background } = theme;

  return (
    <div
      className="relative aspect-[5/2] overflow-hidden"
      style={{ backgroundColor: colors.background }}
    >
      {/* Optional background image */}
      {background && (
        <img
          src={background.url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
      )}

      {/* Simulated UI */}
      <div className="relative p-5 pt-6 space-y-2.5">
        <div
          className="h-3 w-3/5"
          style={{ backgroundColor: colors.text, opacity: 0.6 }}
        />
        <div
          className="h-3 w-2/5"
          style={{ backgroundColor: colors.text, opacity: 0.3 }}
        />
        <div className="pt-1">
          <div
            className="h-6 w-24"
            style={{ backgroundColor: colors.primary }}
          />
        </div>
      </div>

      {/* Color swatches strip */}
      <div className="absolute bottom-0 left-0 right-0 flex h-2">
        <div className="flex-1" style={{ backgroundColor: colors.background }} />
        <div className="flex-1" style={{ backgroundColor: colors.primary }} />
        <div className="flex-1" style={{ backgroundColor: colors.text }} />
      </div>
    </div>
  );
}

/* ── Author avatar + name ── */

function AuthorAvatar({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  return (
    <IridescentAvatar
      src={metadata?.picture}
      alt={metadata?.name ?? genUserName(pubkey)}
      size={28}
    />
  );
}

function AuthorName({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? genUserName(pubkey);

  return (
    <p className="text-[11px] text-pol-text-secondary truncate font-sans">by {name}</p>
  );
}

/* ── Skeleton — sharp corners ── */

function ThemeCardSkeleton() {
  return (
    <div className="bg-polaroid-white p-2 pb-3 shadow-[0_2px_8px_rgba(45,27,78,0.08)]">
      <Skeleton className="aspect-[5/2] w-full skeleton-shimmer animate-shimmer" />
      <div className="px-1 pt-2">
        <div className="flex items-center gap-2.5">
          <Skeleton className="w-7 h-7 rounded-full skeleton-shimmer animate-shimmer" />
          <div className="space-y-1.5 flex-1">
            <Skeleton className="h-4 w-32 skeleton-shimmer animate-shimmer" />
            <Skeleton className="h-3 w-20 skeleton-shimmer animate-shimmer" />
          </div>
        </div>
      </div>
    </div>
  );
}
